import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from "@angular/core";
import { ClippingStudioGraphicEvent } from "app/js/src/domain/clipping-studio/clipping-studio-project";

@Component({
  selector: "evrideo-clipping-studio-logo-editor",
  templateUrl: "./clipping-studio-logo-editor.component.html",
  styleUrls: ["./clipping-studio-logo-editor.component.scss"],
})
export class ClippingStudioLogoEditorComponent implements OnChanges {
  @Input() graphic!: ClippingStudioGraphicEvent;
  @Input() isEditMode = false;
  @Input() currentTimeMs = 0;
  @Input() fps = 30;
  /** Live preview updates; parent should avoid persisting/undo on each emit. */
  @Output() previewChange = new EventEmitter<Partial<ClippingStudioGraphicEvent>>();
  @Output() update = new EventEmitter<Partial<ClippingStudioGraphicEvent>>();
  @Output() close = new EventEmitter<{ saved: boolean }>();

  /** Local draft changes; applied only on Save Changes. */
  private draft: Partial<ClippingStudioGraphicEvent> = {};

  readonly scaleMin = 0.1;
  readonly scaleMax = 3;
  readonly scaleStep = 0.1;
  readonly rotationMin = 0;
  readonly rotationMax = 360;

  get inTimecode(): string {
    return this.formatMsToTimecode(this.graphic.inMs ?? 0);
  }

  get outTimecode(): string {
    return this.formatMsToTimecode(this.graphic.outMs ?? 0);
  }

  get scale(): number {
    const s = this.draft.scale ?? this.graphic.scale;
    return s != null && s >= this.scaleMin && s <= this.scaleMax
      ? s
      : 1;
  }

  get rotationDeg(): number {
    const r = this.draft.rotationDeg ?? this.graphic.rotationDeg;
    return r != null && r >= this.rotationMin && r <= this.rotationMax
      ? r
      : 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const c = changes["graphic"];
    if (!c) return;
    const prevId = (c.previousValue as ClippingStudioGraphicEvent | undefined)?.id;
    const nextId = (c.currentValue as ClippingStudioGraphicEvent | undefined)?.id;
    if (prevId !== nextId) {
      this.draft = {};
    }
  }

  onInTimeBlur(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const ms = this.parseTimecodeToMs(value);
    if (ms != null) {
      this.draft.inMs = ms;
      this.previewChange.emit({ inMs: ms });
    }
  }

  onOutTimeBlur(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const ms = this.parseTimecodeToMs(value);
    if (ms != null) {
      this.draft.outMs = ms;
      this.previewChange.emit({ outMs: ms });
    }
  }

  setInToPlayhead(): void {
    const frameMs = this.getFrameDurationMs();
    const inMs = Math.floor(this.currentTimeMs / frameMs) * frameMs;
    this.draft.inMs = inMs;
    this.previewChange.emit({ inMs });
  }

  setOutToPlayhead(): void {
    const frameMs = this.getFrameDurationMs();
    const frameIndex = Math.floor(this.currentTimeMs / frameMs);
    const outMs = (frameIndex + 1) * frameMs;
    this.draft.outMs = outMs;
    this.previewChange.emit({ outMs });
  }

  onScaleChange(value: number | string): void {
    const n = typeof value === "string" ? parseFloat(value) : value;
    if (!Number.isNaN(n) && n >= this.scaleMin && n <= this.scaleMax) {
      this.draft.scale = n;
      this.previewChange.emit({ scale: n });
    }
  }

  onRotationChange(value: number | string): void {
    const n = typeof value === "string" ? parseInt(value, 10) : value;
    if (!Number.isNaN(n) && n >= this.rotationMin && n <= this.rotationMax) {
      this.draft.rotationDeg = n;
      this.previewChange.emit({ rotationDeg: n });
    }
  }

  onSave(): void {
    if (Object.keys(this.draft).length > 0) {
      this.update.emit(this.draft);
    }
    this.close.emit({ saved: true });
  }

  onClose(): void {
    this.close.emit({ saved: false });
  }

  private formatMsToTimecode(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const millis = Math.floor(ms % 1000);
    const pad = (n: number, len = 2) => n.toString().padStart(len, "0");
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}.${pad(millis, 3)}`;
  }

  private parseTimecodeToMs(str: string): number | null {
    const trimmed = str.trim();
    const match = trimmed.match(/^(\d+):(\d{1,2}):(\d{1,2})\.(\d{1,3})$/);
    if (!match) return null;
    const [, h, m, s, ms] = match.map(Number);
    if (m >= 60 || s >= 60 || ms >= 1000) return null;
    return (h * 3600 + m * 60 + s) * 1000 + ms;
  }

  private getFrameDurationMs(): number {
    const f = this.fps > 0 ? this.fps : 30;
    return 1000 / f;
  }
}
